/**
 * GEDCOM Import Plan Builder
 *
 * Transforms parsed and validated GEDCOM preview data into normalized
 * relational import-ready structures. This is PRE-IMPORT transformation only.
 *
 * Part of Phase 4 - Slice 6: Data Transformation
 */

import type { ParsedPerson } from '../parsers/indiParser';
import type { ResolvedFamily } from '../types/GedcomFamily';
import type { ValidationResult } from '../types/GedcomValidation';
import type {
  GedcomImportPlan,
  ImportPersonCandidate,
  ImportCoupleCandidate,
  ImportParentChildCandidate,
  ImportFactCandidate,
  ImportWarningLink,
} from '../types/GedcomImportPlan';

/**
 * Build complete import plan from parsed GEDCOM data
 *
 * @param persons - Parsed individuals from INDI records
 * @param families - Resolved families from FAM records
 * @param validation - Validation result from preview
 * @returns Complete import plan with normalized entities
 */
export function buildImportPlan(
  persons: ParsedPerson[],
  families: ResolvedFamily[],
  validation: ValidationResult
): GedcomImportPlan {
  // Step 1: Build GEDCOM ID → Temp ID mapping for stable references
  const gedcomToTempId = new Map<string, string>();

  // Step 2: Transform persons
  const personCandidates = persons.map((person) => {
    const tempId = generateStableTempId('person', person.gedcomId);
    gedcomToTempId.set(person.gedcomId, tempId);
    return transformPerson(person, tempId, validation);
  });

  // Step 3: Transform families/couples
  const coupleCandidates = families.map((family) => {
    const tempId = generateStableTempId('couple', family.familyId);
    return transformCouple(family, tempId, gedcomToTempId);
  });

  // Step 4: Build parent-child links
  const parentChildLinks = buildParentChildLinks(families, gedcomToTempId);

  // Step 5: Extract facts from persons and couples
  const personFacts = extractPersonFacts(persons, gedcomToTempId);
  const coupleFacts = extractCoupleFacts(families);
  const allFacts = [...personFacts, ...coupleFacts];

  // Step 6: Build warning links
  const warningLinks = buildWarningLinks(validation, gedcomToTempId);

  // Step 7: Build import ordering metadata
  const importOrder = {
    persons_count: personCandidates.length,
    couples_count: coupleCandidates.length,
    links_count: parentChildLinks.length,
    facts_count: allFacts.length,
  };

  // Step 8: Calculate summary statistics
  const blockedCount = validation.summary.errorCount;
  const warningCount = validation.summary.warningCount + validation.summary.infoCount;
  const readyForImport = validation.status !== 'blocked';

  return {
    persons: personCandidates,
    couples: coupleCandidates,
    parent_child_links: parentChildLinks,
    facts: allFacts,
    warnings: warningLinks,
    import_order: importOrder,
    summary: {
      total_entities:
        personCandidates.length +
        coupleCandidates.length +
        parentChildLinks.length +
        allFacts.length,
      blocked_count: blockedCount,
      warning_count: warningCount,
      ready_for_import: readyForImport,
    },
  };
}

/**
 * Generate stable temporary ID from GEDCOM ID
 * Uses deterministic hashing to ensure same GEDCOM ID always maps to same temp ID
 *
 * @param entityType - Type of entity (person, couple, etc.)
 * @param gedcomId - GEDCOM identifier
 * @returns Stable temporary UUID-like string
 */
function generateStableTempId(entityType: string, gedcomId: string): string {
  // For preview/planning purposes, we use a deterministic ID
  // Format: "temp-{type}-{hash}" where hash is based on GEDCOM ID
  const hash = simpleHash(gedcomId);
  return `temp-${entityType}-${hash}`;
}

/**
 * Simple hash function for deterministic ID generation
 */
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36);
}

/**
 * Transform ParsedPerson into ImportPersonCandidate
 */
function transformPerson(
  person: ParsedPerson,
  tempId: string,
  validation: ValidationResult
): ImportPersonCandidate {
  // Find warnings related to this person
  const personWarnings = validation.issues
    .filter((issue) => issue.gedcomId === person.gedcomId)
    .map((issue) => issue.message);

  // Infer living status (rough heuristic: no death date and birth < 110 years ago)
  let isLiving: boolean | null = null;
  if (person.birthYear) {
    const currentYear = new Date().getFullYear();
    const age = currentYear - person.birthYear;
    if (!person.deathDate && !person.deathYear && age < 110) {
      isLiving = true;
    } else if (person.deathDate || person.deathYear) {
      isLiving = false;
    }
  }

  return {
    temp_id: tempId,
    gedcom_id: person.gedcomId,
    first_name: person.givenName || 'Unknown',
    last_name: person.surname,
    full_name: person.displayName,
    gender: person.gender,
    birth_date_raw: person.birthDate,
    death_date_raw: person.deathDate,
    birth_year: person.birthYear,
    death_year: person.deathYear,
    birth_qualifier: person.birthQualifier,
    death_qualifier: person.deathQualifier,
    nickname: null, // Not extracted from current NAME parsing
    notes: null, // Not extracted in current implementation
    is_living: isLiving,
    warnings: personWarnings,
  };
}

/**
 * Transform ResolvedFamily into ImportCoupleCandidate
 */
function transformCouple(
  family: ResolvedFamily,
  tempId: string,
  gedcomToTempId: Map<string, string>
): ImportCoupleCandidate {
  // Resolve spouse temp IDs
  const person1TempId = family.husband?.gedcomId
    ? gedcomToTempId.get(family.husband.gedcomId) || null
    : null;
  const person2TempId = family.wife?.gedcomId
    ? gedcomToTempId.get(family.wife.gedcomId) || null
    : null;

  // Resolve child temp IDs
  const childTempIds = family.children
    .map((child) => gedcomToTempId.get(child.gedcomId))
    .filter((id): id is string => id !== undefined);

  // Build flags
  const flags = {
    one_parent_family: (!!person1TempId && !person2TempId) || (!person1TempId && !!person2TempId),
    unresolved_spouse: (family.unresolvedRefs?.length || 0) > 0,
    unresolved_children: family.children.length < (family.unresolvedRefs?.length || 0),
    childless_family: family.children.length === 0,
  };

  return {
    temp_id: tempId,
    gedcom_id: family.familyId,
    person1_temp_id: person1TempId,
    person2_temp_id: person2TempId,
    person1_gedcom_id: family.husband?.gedcomId || null,
    person2_gedcom_id: family.wife?.gedcomId || null,
    marriage_date_raw: family.marriageDate,
    divorce_date_raw: family.divorceDate,
    marriage_year: family.marriageYear,
    divorce_year: family.divorceYear,
    marriage_qualifier: family.marriageQualifier,
    divorce_qualifier: family.divorceQualifier,
    is_married: !!family.marriageDate || !!family.marriageYear,
    has_ended: !!family.divorceDate || !!family.divorceYear,
    flags,
    child_temp_ids: childTempIds,
  };
}

/**
 * Build parent-child relationship links from families
 * Generates flat collection without duplicates
 */
function buildParentChildLinks(
  families: ResolvedFamily[],
  gedcomToTempId: Map<string, string>
): ImportParentChildCandidate[] {
  const links: ImportParentChildCandidate[] = [];
  const seenLinks = new Set<string>(); // Track duplicates

  families.forEach((family) => {
    const fatherTempId = family.husband?.gedcomId
      ? gedcomToTempId.get(family.husband.gedcomId)
      : null;
    const motherTempId = family.wife?.gedcomId
      ? gedcomToTempId.get(family.wife.gedcomId)
      : null;

    family.children.forEach((child) => {
      const childTempId = gedcomToTempId.get(child.gedcomId);

      if (!childTempId) return; // Skip if child not found

      // Create father link
      if (fatherTempId) {
        const linkKey = `${fatherTempId}-${childTempId}-father`;
        if (!seenLinks.has(linkKey)) {
          links.push({
            temp_id: generateStableTempId('link', linkKey),
            family_gedcom_id: family.familyId,
            parent_temp_id: fatherTempId,
            child_temp_id: childTempId,
            relationship_source: 'gedcom',
            parent_role: 'father',
          });
          seenLinks.add(linkKey);
        }
      }

      // Create mother link
      if (motherTempId) {
        const linkKey = `${motherTempId}-${childTempId}-mother`;
        if (!seenLinks.has(linkKey)) {
          links.push({
            temp_id: generateStableTempId('link', linkKey),
            family_gedcom_id: family.familyId,
            parent_temp_id: motherTempId,
            child_temp_id: childTempId,
            relationship_source: 'gedcom',
            parent_role: 'mother',
          });
          seenLinks.add(linkKey);
        }
      }
    });
  });

  return links;
}

/**
 * Extract person facts (BIRT, DEAT) from individuals
 */
function extractPersonFacts(
  persons: ParsedPerson[],
  gedcomToTempId: Map<string, string>
): ImportFactCandidate[] {
  const facts: ImportFactCandidate[] = [];

  persons.forEach((person) => {
    const ownerTempId = gedcomToTempId.get(person.gedcomId);
    if (!ownerTempId) return;

    // BIRT fact
    if (person.birthDate || person.birthYear) {
      facts.push({
        temp_id: generateStableTempId('fact', `${person.gedcomId}-BIRT`),
        owner_type: 'person',
        owner_temp_id: ownerTempId,
        fact_type: 'BIRT',
        date_raw: person.birthDate,
        year: person.birthYear,
        date_qualifier: person.birthQualifier,
        place_raw: null, // Not extracted in current implementation
        value_raw: null,
        gedcom_source_id: person.gedcomId,
        notes: null,
      });
    }

    // DEAT fact
    if (person.deathDate || person.deathYear) {
      facts.push({
        temp_id: generateStableTempId('fact', `${person.gedcomId}-DEAT`),
        owner_type: 'person',
        owner_temp_id: ownerTempId,
        fact_type: 'DEAT',
        date_raw: person.deathDate,
        year: person.deathYear,
        date_qualifier: person.deathQualifier,
        place_raw: null,
        value_raw: null,
        gedcom_source_id: person.gedcomId,
        notes: null,
      });
    }
  });

  return facts;
}

/**
 * Extract couple facts (MARR, DIV) from families
 */
function extractCoupleFacts(
  families: ResolvedFamily[]
): ImportFactCandidate[] {
  const facts: ImportFactCandidate[] = [];

  families.forEach((family) => {
    const coupleTempId = generateStableTempId('couple', family.familyId);

    // MARR fact
    if (family.marriageDate || family.marriageYear) {
      facts.push({
        temp_id: generateStableTempId('fact', `${family.familyId}-MARR`),
        owner_type: 'couple',
        owner_temp_id: coupleTempId,
        fact_type: 'MARR',
        date_raw: family.marriageDate,
        year: family.marriageYear,
        date_qualifier: family.marriageQualifier,
        place_raw: null,
        value_raw: null,
        gedcom_source_id: family.familyId,
        notes: null,
      });
    }

    // DIV fact
    if (family.divorceDate || family.divorceYear) {
      facts.push({
        temp_id: generateStableTempId('fact', `${family.familyId}-DIV`),
        owner_type: 'couple',
        owner_temp_id: coupleTempId,
        fact_type: 'DIV',
        date_raw: family.divorceDate,
        year: family.divorceYear,
        date_qualifier: family.divorceQualifier,
        place_raw: null,
        value_raw: null,
        gedcom_source_id: family.familyId,
        notes: null,
      });
    }
  });

  return facts;
}

/**
 * Build warning links from validation issues
 */
function buildWarningLinks(
  validation: ValidationResult,
  gedcomToTempId: Map<string, string>
): ImportWarningLink[] {
  return validation.issues
    .filter((issue) => issue.severity === 'warning' || issue.severity === 'info')
    .map((issue) => ({
      gedcom_id: issue.gedcomId || 'UNKNOWN',
      temp_id: issue.gedcomId ? gedcomToTempId.get(issue.gedcomId) || null : null,
      message: issue.message,
      severity: (issue.severity === 'warning' ? 'warning' : 'info') as 'warning' | 'info',
    }));
}
